// Analytics wrapper — the single choke point every event goes through,
// ported from apps/old-web/lib/analytics.ts. The taxonomy below derives from
// THIS app's real screens (splash → provider signup / docs / api access →
// dashboard), not the legacy job/quote taxonomy.
//
// fire() ordering matters:
//  1. config check (sync, free) — env unset means no geo fetch, no consent
//     work, no SDK bytes; every call is a clean no-op.
//  2. consent check — GDPR/unknown visitors without an accept produce nothing
//     and the firebase SDK is never even imported.
//  3. lazy SDK init + logEvent.
//
// PARAMS ARE PII-FREE. Never emails, tokens, session data, names, or
// addresses. Screen/section identifiers and locale codes only.

import { hasAnalyticsConsent } from "./consent";
import {
  getFirebaseAnalytics,
  isAnalyticsConfigured,
  setFirebaseAnalyticsCollectionAllowed
} from "./firebase";

type EventParams = Record<string, string | number | boolean>;

/** Never expose auth/recovery query or fragment material to analytics. */
export function safeAnalyticsPageLocation(): string {
  if (hasPrivateOrderPath(new URL(window.location.href))) return `${window.location.origin}/o/[REDACTED]`;
  return `${window.location.origin}${window.location.pathname}`;
}

function hasPrivateOrderPath(url: URL): boolean {
  return /^\/o\/[^/]+/i.test(url.pathname);
}

const SENSITIVE_QUERY_KEYS = new Set([
  "access_token",
  "code",
  "email",
  "refresh_token",
  "return",
  "token"
]);

function hasSensitiveUrlMaterial(url: URL): boolean {
  if (hasPrivateOrderPath(url)) return true;
  if ([...url.searchParams.keys()].some((key) => SENSITIVE_QUERY_KEYS.has(key.toLowerCase()))) {
    return true;
  }
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  return [...hashParams.keys()].some((key) => SENSITIVE_QUERY_KEYS.has(key.toLowerCase()));
}

export function suspendAnalyticsForSensitiveLocation(target: string | URL): boolean {
  const url = target instanceof URL ? target : new URL(target, window.location.href);
  const sensitive = hasSensitiveUrlMaterial(url);
  if (sensitive) setFirebaseAnalyticsCollectionAllowed(false);
  return sensitive;
}

const NAVIGATION_GUARD = Symbol.for("tinkr.analytics.navigation-guard");

type GuardedHistory = History & {
  [NAVIGATION_GUARD]?: {
    inspect: (target: string | URL) => void;
  };
};

/**
 * Firebase Enhanced Measurement can observe History API changes even when
 * automatic page_view is disabled in this SDK instance. Install our guard
 * first so every query/fragment transition suspends collection before any
 * Firebase listener can see the browser's raw URL. A later custom event can
 * safely re-enable collection because it supplies a query-free page_location.
 */
function installNavigationGuard(): void {
  const history = window.history as GuardedHistory;
  let state = history[NAVIGATION_GUARD];
  if (!state) {
    state = { inspect: () => undefined };
    history[NAVIGATION_GUARD] = state;
    const pushState = history.pushState;
    const replaceState = history.replaceState;
    history.pushState = function guardedPushState(data, unused, url) {
      if (url != null) state?.inspect(new URL(String(url), window.location.href));
      return pushState.call(this, data, unused, url);
    };
    history.replaceState = function guardedReplaceState(data, unused, url) {
      if (url != null) state?.inspect(new URL(String(url), window.location.href));
      return replaceState.call(this, data, unused, url);
    };
    const inspectCurrentLocation = (): void => state?.inspect(window.location.href);
    window.addEventListener("popstate", inspectCurrentLocation);
    window.addEventListener("hashchange", inspectCurrentLocation);
  }
  state.inspect = (target): void => {
    const url = target instanceof URL ? target : new URL(target, window.location.href);
    // Guard every query/fragment here, not only known secrets. Dashboard ids
    // and future parameters must never reach automatic history measurement.
    if (url.search.length > 0 || url.hash.length > 0 || hasPrivateOrderPath(url)) {
      setFirebaseAnalyticsCollectionAllowed(false);
    }
  };
}

export async function fire(eventName: string, params?: EventParams): Promise<void> {
  try {
    if (typeof window === "undefined") return;
    if (suspendAnalyticsForSensitiveLocation(window.location.href)) return;
    if (!isAnalyticsConfigured()) return;
    if (!(await hasAnalyticsConsent())) return;
    const pageLocation = safeAnalyticsPageLocation();
    installNavigationGuard();
    const analytics = await getFirebaseAnalytics(pageLocation);
    if (!analytics) return;
    // Consent can be withdrawn while the SDK downloads/initializes.
    if (!(await hasAnalyticsConsent()) || suspendAnalyticsForSensitiveLocation(window.location.href)) {
      setFirebaseAnalyticsCollectionAllowed(false);
      return;
    }
    setFirebaseAnalyticsCollectionAllowed(true);
    const { logEvent } = await import("firebase/analytics");
    if (!(await hasAnalyticsConsent()) || suspendAnalyticsForSensitiveLocation(window.location.href)) {
      setFirebaseAnalyticsCollectionAllowed(false);
      return;
    }
    logEvent(analytics, eventName, { ...params, page_location: pageLocation });
  } catch {
    // Analytics must never break the app.
  }
}

export const track = {
  /** Top-level screen transition (state-machine SPA: screen id, not a path). */
  pageView: (screen: string): void => {
    void fire("page_view", { page_path: `/${screen}` });
  },

  /** A marketing CTA press, with which CTA it was. */
  ctaClicked: (cta: string): void => {
    void fire("cta_clicked", { cta });
  },

  /** The auth screen opened in signup mode (provider acquisition funnel). */
  providerSignupStarted: (): void => {
    void fire("provider_signup_started");
  },

  /** A docs-portal section became active. Section ids are public slugs. */
  docsViewed: (section: string): void => {
    void fire("docs_viewed", { section });
  },

  /** The visitor switched UI language. */
  localeChanged: (locale: string): void => {
    void fire("locale_changed", { locale });
  }
};
